import { useState, useCallback } from 'react';
import { isValidEmailFormat, isValidUsernameFormat, isValidFullNameFormat } from '../utils/validation';

/**
 * Centralized validation hook for form fields
 * Provides consistent validation logic across registration, account editing, and other forms
 * 
 * @param {Object} initialData - Initial form data object
 * @returns {Object} Validation state and functions
 */
export const useValidation = (initialData = {}) => {
  const [errors, setErrors] = useState({});

  /**
   * Validate full name field
   * @param {string} value - Full name value to validate
   * @returns {string|null} Error message or null if valid
   */
  const validateFullName = useCallback((value) => {
    if (!value || value.length < 2) return 'Full name must be at least 2 characters';
    if (value.length > 50) return 'Full name cannot exceed 50 characters';
    if (!isValidFullNameFormat(value)) return 'Full name can only contain letters, spaces, hyphens, and apostrophes';
    return null;
  }, []);

  /**
   * Validate email field
   * @param {string} value - Email value to validate
   * @returns {string|null} Error message or null if valid
   */
  const validateEmail = useCallback((value) => {
    if (!value) return 'Email is required.';
    if (value.length > 100) return 'Email cannot exceed 100 characters';
    if (!isValidEmailFormat(value)) return 'Please enter a valid email address';
    return null;
  }, []);

  /**
   * Validate new email field (same as email but for email change context)
   * @param {string} value - New email value to validate
   * @param {string} currentEmail - Current email address to compare against
   * @returns {string|null} Error message or null if valid
   */
  const validateNewEmail = useCallback((value, currentEmail = '') => {
    if (!value) return 'New email is required.';
    if (value.length > 100) return 'New email cannot exceed 100 characters';
    if (!isValidEmailFormat(value)) return 'Please enter a valid email address';
    if (currentEmail && value.toLowerCase() === currentEmail.toLowerCase()) {
      return 'New email must be different from your current email address.';
    }
    return null;
  }, []);

  /**
   * Validate username field
   * @param {string} value - Username value to validate
   * @returns {string|null} Error message or null if valid
   */
  const validateUsername = useCallback((value) => {
    if (!value || value.length < 3) return 'Username must be at least 3 characters';
    if (value.length > 20) return 'Username cannot exceed 20 characters';
    if (!isValidUsernameFormat(value)) return 'Username can only contain letters, numbers, and underscores';
    return null;
  }, []);

  /**
   * Validate password field
   * @param {string} value - Password value to validate
   * @param {Object} strength - Password strength object from usePasswordStrength
   * @returns {string|null} Error message or null if valid
   */
  const validatePassword = useCallback((value, strength = null) => {
    if (!value) return 'Password is required.';
    if (value.length > 128) return 'Password cannot exceed 128 characters';
    if (strength && !strength.isValid) return ' '; // Show border only, tooltip covers details
    return null;
  }, []);

  /**
   * Validate password confirmation
   * @param {string} password - Password value
   * @param {string} confirmation - Password confirmation value
   * @returns {string|null} Error message or null if valid
   */
  const validatePasswordConfirmation = useCallback((password, confirmation) => {
    if (password && confirmation && password !== confirmation) {
      return 'Password confirmation does not match.';
    }
    return null;
  }, []);

  /**
   * Validate country field
   * @param {string} value - Country code value to validate
   * @returns {string|null} Error message or null if valid
   */
  const validateCountry = useCallback((value) => {
    if (!value) return 'Country is required.';
    if (value.length !== 3) return 'Country code must be exactly 3 characters';
    if (!/^[A-Z]{3}$/.test(value)) return 'Country code must be a valid 3-letter code';
    return null;
  }, []);

  /**
   * Validate terms acceptance
   * @param {boolean} value - Terms acceptance value
   * @returns {string|null} Error message or null if valid
   */
  const validateTerms = useCallback((value) => {
    if (!value) return 'You must accept the terms and conditions to continue.';
    return null;
  }, []);

  /**
   * Validate current password field
   * @param {string} value - Current password value
   * @returns {string|null} Error message or null if valid
   */
  const validateCurrentPassword = useCallback((value) => {
    if (!value) return 'Current password is required.';
    return null;
  }, []);

  /**
   * Validate field on blur
   * @param {string} fieldName - Name of the field to validate
   * @param {string} value - Value to validate
   * @param {Object} additionalData - Additional data needed for validation (e.g., password strength)
   */
  const validateFieldOnBlur = useCallback((fieldName, value, additionalData = {}) => {
    let error = null;

    // Only validate if field has content (prevents premature validation)
    if (!value || value.trim() === '') {
      // Clear any existing error for empty fields
      setErrors(prev => ({ ...prev, [fieldName]: null }));
      return;
    }

    switch (fieldName) {
      case 'fullName':
        error = validateFullName(value);
        break;
      case 'email':
        error = validateEmail(value);
        break;
      case 'new_email':
        error = validateNewEmail(value, additionalData.currentEmail);
        break;
      case 'username':
        error = validateUsername(value);
        break;
      case 'current_password':
        error = validateCurrentPassword(value);
        break;
      case 'password':
        error = validatePassword(value, additionalData.strength);
        break;
      case 'password_confirmation':
        error = validatePasswordConfirmation(additionalData.password, value);
        break;
      case 'country':
        error = validateCountry(value);
        break;
      case 'acceptTerms':
        error = validateTerms(value);
        break;
      default:
        break;
    }

    if (error) {
      setErrors(prev => ({ ...prev, [fieldName]: error }));
    } else {
      // Clear error if validation passes
      setErrors(prev => ({ ...prev, [fieldName]: null }));
    }
  }, [validateFullName, validateEmail, validateUsername, validateCurrentPassword, validatePassword, validatePasswordConfirmation, validateCountry, validateTerms, validateNewEmail]);

  /**
   * Validate all fields for form submission
   * @param {Object} formData - Complete form data object
   * @param {Object} additionalData - Additional data needed for validation
   * @returns {Object} Object with isValid boolean and errors object
   */
  const validateForm = useCallback((formData, additionalData = {}) => {
    const submitErrors = {};

    // Validate each field
    if (formData.fullName !== undefined) {
      const error = validateFullName(formData.fullName);
      if (error) submitErrors.fullName = error;
    }

    if (formData.email !== undefined) {
      const error = validateEmail(formData.email);
      if (error) submitErrors.email = error;
    }

    if (formData.new_email !== undefined) {
      const error = validateNewEmail(formData.new_email, formData.email);
      if (error) submitErrors.new_email = error;
    }

    if (formData.username !== undefined) {
      const error = validateUsername(formData.username);
      if (error) submitErrors.username = error;
    }

    if (formData.current_password !== undefined) {
      const error = validateCurrentPassword(formData.current_password);
      if (error) submitErrors.current_password = error;
    }

    if (formData.password !== undefined) {
      const error = validatePassword(formData.password, additionalData.strength);
      if (error) submitErrors.password = error;
    }

    if (formData.password_confirmation !== undefined && formData.password) {
      const error = validatePasswordConfirmation(formData.password, formData.password_confirmation);
      if (error) submitErrors.password_confirmation = error;
    }

    if (formData.country !== undefined) {
      const error = validateCountry(formData.country);
      if (error) submitErrors.country = error;
    }

    if (formData.acceptTerms !== undefined) {
      const error = validateTerms(formData.acceptTerms);
      if (error) submitErrors.acceptTerms = error;
    }

    // Update errors state
    setErrors(submitErrors);

    return {
      isValid: Object.keys(submitErrors).length === 0,
      errors: submitErrors
    };
  }, [validateFullName, validateEmail, validateUsername, validateCurrentPassword, validatePassword, validatePasswordConfirmation, validateCountry, validateTerms, validateNewEmail]);

  /**
   * Clear error for a specific field
   * @param {string} fieldName - Name of the field to clear error for
   */
  const clearFieldError = useCallback((fieldName) => {
    setErrors(prev => ({ ...prev, [fieldName]: null }));
  }, []);

  /**
   * Clear all errors
   */
  const clearAllErrors = useCallback(() => {
    setErrors({});
  }, []);

  /**
   * Set a specific error manually
   * @param {string} fieldName - Name of the field
   * @param {string} errorMessage - Error message to set
   */
  const setFieldError = useCallback((fieldName, errorMessage) => {
    setErrors(prev => ({ ...prev, [fieldName]: errorMessage }));
  }, []);

  return {
    errors,
    validateFullName,
    validateEmail,
    validateNewEmail,
    validateUsername,
    validatePassword,
    validatePasswordConfirmation,
    validateCountry,
    validateTerms,
    validateCurrentPassword,
    validateFieldOnBlur,
    validateForm,
    clearFieldError,
    clearAllErrors,
    setFieldError,
  };
};
